import { useCallback, useMemo, useState } from 'react';
import { message } from 'antd';
import { get } from 'lodash-es';
import { useTranslate } from '@tolgee/react';
import { isTiKTok } from 'lib/utils/isTikTok';
import { toUpperCase } from 'utils/toLowerCase';
import { updateVoucherStatus, voucher as fetchVoucher } from 'services/api/conversation';
import { useAuthorization } from 'components/Authorization';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useGetPermission } from './useNewPermisson';
import { systemLanguageMap } from 'views/Conversation/Sider/constants';

/** 仅需权限时可直接传 { channel }，执行作废/暂停需完整 conversation（含 storeId） */
export type VoucherOperateConversation =
  | Conversation.Conversation
  | { channel?: General.PlatformUpperCaseTunnel; storeId?: string }
  | undefined;

export function useVoucherCanCreate(conversation: VoucherOperateConversation) {
  const getPermission = useGetPermission();

  const canCreate = useMemo(() => {
    const permission = getPermission('编辑优惠券');
    if (!permission) {
      return false;
    }
    const isTikTok = isTiKTok(conversation?.channel);
    return !isTikTok;
  }, [conversation?.channel, getPermission]);

  return { canCreate };
}

export function useVoucherOperate(
  voucher: Conversation.Voucher.Vo | undefined,
  conversation: VoucherOperateConversation,
) {
  const { t } = useTranslate();
  const systemLanguage = useSystemLanguageValue();
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { id: operatorId = 0 } = payload || {};
  const getPermission = useGetPermission();

  const [voidLoading, setVoidLoading] = useState(false);
  const [suspendResumeLoading, setSuspendResumeLoading] = useState(false);

  const language = systemLanguageMap[systemLanguage];

  const canVoid = useMemo(() => {
    if (!voucher) return false;
    return voucher.platform === 'shopee' && voucher.voucherStatus !== 'SCHEDULED';
  }, [voucher]);

  const canSuspendResume = useMemo(() => {
    if (!voucher) return false;
    return voucher.platform !== 'shopee';
  }, [voucher]);

  const { canCreate, canCopy, canEdit } = useMemo(() => {
    const permission = getPermission('编辑优惠券');
    if (!permission) {
      return { canCreate: false, canCopy: false, canEdit: false };
    }
    const isTikTok = isTiKTok(conversation?.channel);
    return {
      canCreate: !isTikTok,
      canCopy: !isTikTok,
      canEdit: !isTikTok,
    };
  }, [conversation?.channel, getPermission]);

  const voidVoucher = useCallback(
    (params: { onSuccess?: () => void }) => {
      if (!voucher || !conversation) return;
      const { internalVoucherId } = voucher;
      setVoidLoading(true);
      updateVoucherStatus({
        internalVoucherId: parseInt(internalVoucherId, 10),
        type: 'END',
        operatorId,
        language,
      })
        .then(() => {
          message.success(t('voucher.已作废'));
          params.onSuccess?.();
        })
        .catch((err) => {
          if (get(err, 'response.data.code') === 499 && get(err, 'response.data.msg')) {
            message.error(get(err, 'response.data.msg'));
          } else {
            message.error(t('voucher.作废失败，请稍后再试'));
          }
        })
        .finally(() => {
          setVoidLoading(false);
        });
    },
    [conversation, language, operatorId, t, voucher],
  );

  const suspendResumeVoucher = useCallback(
    (params: { onSuccess?: (updated: Conversation.Voucher.Vo) => void }) => {
      if (!voucher || !conversation) return;
      const storeId = conversation.storeId;
      if (storeId == null) return;
      const { internalVoucherId, externalVoucherId, suspendFlag, platform } = voucher;
      setSuspendResumeLoading(true);
      updateVoucherStatus({
        internalVoucherId: parseInt(internalVoucherId, 10),
        type: suspendFlag ? 'RESUME' : 'SUSPEND',
        operatorId,
        language,
      })
        .then(() =>
          fetchVoucher({
            internal_store_id: String(storeId),
            external_voucher_id: externalVoucherId,
            platform: toUpperCase(platform),
          }),
        )
        .then((res) => {
          message.success(suspendFlag ? t('voucher.开启成功') : t('voucher.已暂停'));
          if (res) {
            params.onSuccess?.(res);
          }
        })
        .catch((err) => {
          if (get(err, 'response.data.code') === 499 && get(err, 'response.data.msg')) {
            message.error(get(err, 'response.data.msg'));
          } else {
            message.error(
              suspendFlag ? t('voucher.开启失败，请稍后再试') : t('voucher.暂停失败，请稍后再试'),
            );
          }
        })
        .finally(() => {
          setSuspendResumeLoading(false);
        });
    },
    [conversation, language, operatorId, t, voucher],
  );

  return {
    canVoid,
    canSuspendResume,
    canCreate,
    canCopy,
    canEdit,
    voidVoucher,
    suspendResumeVoucher,
    voidLoading,
    suspendResumeLoading,
  };
}
